<template>
  <div class="modal-overlay" @click.self="close">
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ isEdit ? '编辑课程' : '添加课程' }}</h2>
        <button class="close-btn" @click="close">&times;</button>
      </div>

      <form @submit.prevent="submit">
        <div class="form-group">
          <label for="name">课程名称</label>
          <input id="name" v-model="form.name" required>
        </div>

        <div class="form-group">
          <label for="subject">科目</label>
          <input id="subject" v-model="form.subject" required>
        </div>

        <div class="form-group">
          <label for="grade">年级</label>
          <select id="grade" v-model="form.grade" required>
            <option v-for="option in gradeOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label for="status">状态</label>
          <select id="status" v-model="form.status" required>
            <option v-for="option in statusOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label class="section-label">上课日期</label>
          <div class="days-grid">
            <div v-for="day in days" :key="day.value" class="day-option">
              <input
                type="checkbox"
                :id="`day-${day.value}`"
                :value="day.value"
                v-model="form.checkedDays"
                class="day-checkbox"
              >
              <label :for="`day-${day.value}`" class="day-label">
                {{ day.label }}
              </label>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="button" class="cancel-btn" @click="close">取消</button>
          <button type="submit" class="submit-btn">提交</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { CREATE_COURSE, UPDATE_COURSE } from '../graphql/queries';

const props = defineProps({
  course: Object,
  isEdit: Boolean
});

const emit = defineEmits(['close', 'saved']);

const gradeOptions = [
  { label: '一年级', value: 1 },
  { label: '二年级', value: 2 },
  { label: '三年级', value: 3 },
  { label: '四年级', value: 4 },
  { label: '五年级', value: 5 },
  { label: '六年级', value: 6 },
  { label: '國一', value: 7 },
  { label: '國二', value: 8 },
  { label: '國三', value: 9 },
  { label: '高一', value: 10 },
  { label: '高二', value: 11 },
  { label: '高三', value: 12 },
];

const statusOptions = [
  { label: '準備中', value: 'PREPARING' },
  { label: '上課中', value: 'ONGOING' },
  { label: '已結束', value: 'COMPLETED' },
  { label: '暫停', value: 'PAUSED' },
];

const days = [
  { label: '週一', value: 'monday' },
  { label: '週二', value: 'tuesday' },
  { label: '週三', value: 'wednesday' },
  { label: '週四', value: 'thursday' },
  { label: '週五', value: 'friday' },
  { label: '週六', value: 'saturday' },
  { label: '週日', value: 'sunday' }
];

const form = ref({
  name: '',
  subject: '',
  grade: 7,
  status: 'PREPARING',
  checkedDays: []
});

const { mutate, loading } = useMutation(
  props.isEdit ? UPDATE_COURSE : CREATE_COURSE
);

watch(() => props.course, (course) => {
  if (course) {
    form.value = {
      name: course.name,
      subject: course.subject,
      grade: course.grade,
      status: course.status,
      checkedDays: getCheckedDaysFromCourse(course)
    };
  } else {
    resetForm();
  }
}, { immediate: true });

function getCheckedDaysFromCourse(course) {
  const days = [];
  if (course.on_monday) days.push('monday');
  if (course.on_tuesday) days.push('tuesday');
  if (course.on_wednesday) days.push('wednesday');
  if (course.on_thursday) days.push('thursday');
  if (course.on_friday) days.push('friday');
  if (course.on_saturday) days.push('saturday');
  if (course.on_sunday) days.push('sunday');
  return days;
}

function resetForm() {
  form.value = {
    name: '',
    subject: '',
    grade: 7,
    status: 'PREPARING',
    checkedDays: []
  };
}

function close() {
  emit('close');
}

function submit() {
  const variables = {
    ...(props.isEdit && { course_id: props.course.course_id }),
    name: form.value.name,
    subject: form.value.subject,
    grade: form.value.grade,
    status: form.value.status,
    on_monday: form.value.checkedDays.includes('monday'),
    on_tuesday: form.value.checkedDays.includes('tuesday'),
    on_wednesday: form.value.checkedDays.includes('wednesday'),
    on_thursday: form.value.checkedDays.includes('thursday'),
    on_friday: form.value.checkedDays.includes('friday'),
    on_saturday: form.value.checkedDays.includes('saturday'),
    on_sunday: form.value.checkedDays.includes('sunday')
  };

  mutate(variables).then(() => {
    emit('saved');
  });
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.course-form {
  padding: 20px;
}

form {
  padding: 20px;
}

.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  flex: 1;
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #444;
  font-size: 0.9rem;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  transition: border-color 0.3s;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.section-label {
  display: block;
  margin-bottom: 12px;
  font-weight: 600;
  color: #444;
  font-size: 0.95rem;
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 8px;
}

.day-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.day-checkbox {
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: #4CAF50; /* Makes checkbox green when checked */
}

.day-label {
  cursor: pointer;
  font-size: 0.9rem;
  color: #555;
  white-space: nowrap;
}

/* .days-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.day-item {
  display: flex;
  align-items: center;
}

.day-checkbox {
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid #ddd;
  border-radius: 4px;
  margin-right: 8px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
}

.day-checkbox:checked {
  background-color: #4CAF50;
  border-color: #4CAF50;
}

.day-checkbox:checked::after {
  content: '✓';
  position: absolute;
  color: white;
  font-size: 12px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.day-label {
  cursor: pointer;
  font-size: 0.9rem;
  color: #555;
} */

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.btn {
  padding: 10px 20px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.95rem;
}

.cancel-btn {
  background-color: #f5f5f5;
  color: #555;
  border: 1px solid #ddd;
}

.cancel-btn:hover {
  background-color: #eaeaea;
}

.submit-btn {
  background-color: #4CAF50;
  color: white;
  border: none;
}

.submit-btn:hover:not(:disabled) {
  background-color: #43a047;
}

.submit-btn:disabled {
  background-color: #a5d6a7;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
  }
  
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .days-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  /* .days-container {
    grid-template-columns: repeat(3, 1fr);
  } */
}

@media (max-width: 480px) {
  .days-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  /* .days-container {
    grid-template-columns: repeat(2, 1fr);
  } */
}
</style>